import { computed } from 'vue'
import { useRouter, type Router } from 'vue-router'
import { routes } from '@app/router/routes'
import {
  useAccountStatusQuery,
  useReInitiateKycMutation,
  INVESTOR_STATUS,
  ACCOUNT_STATE,
  PAUSED_REASON,
} from '@modules/account'
import { useFiatWalletQuery, useWalletStatusQuery } from '@modules/wallets'
import type { EligibilityFix } from '../components/InvestEligibilityBlocker.vue'

type EligibilityFixWithAction = EligibilityFix & { onCta: () => void }

interface FixContext {
  router: Router
  refetchWallet: () => void
  investorStatus: string | undefined
  accessRestricted: boolean
  // Authoritative server-owned screen discriminator + paused cause. The KYC
  // gate below leans on these (not on kycStatus) because a re-KYC user keeps
  // kycStatus='KYC_COMPLETED' from their earlier verification.
  accountState: string
  pausedReason: string | null
  // Re-initiates KYC then routes into the resubmit wizard. Mirrors the
  // dashboard's StateReKycRequiredContainer handler.
  resubmitKyc: () => void
}

/**
 * Eligibility precedence: account restricted → re-KYC required → KYC under
 * review → KYC incomplete → bank → wallet. We surface the FIRST unmet step so
 * the user makes one decision at a time.
 */
const buildFix = (
  kycComplete: boolean,
  bankComplete: boolean,
  walletActive: boolean,
  {
    router,
    refetchWallet,
    investorStatus,
    accessRestricted,
    accountState,
    pausedReason,
    resubmitKyc,
  }: FixContext,
): EligibilityFixWithAction | null => {
  // Highest precedence: a restricted account can never invest, regardless of
  // KYC / bank / wallet readiness. Swap the eligibility step for a
  // contact-support blocker.
  if (accessRestricted) {
    return {
      icon: 'lock',
      title: 'Account access restricted',
      description:
        'Your account access has been restricted. Please contact support for more information.',
      ctaLabel: 'Contact support',
      onCta: () => router.push(routes.HELP),
    }
  }
  // Re-KYC required: the user's verification was rejected (admin re-initiate /
  // active re-verify) or expired. They cannot invest even with a verified bank
  // and an active wallet — the path forward is to resubmit, not to add a bank.
  if (accountState === ACCOUNT_STATE.REKYC_REJECTED) {
    return {
      icon: 'shield',
      title: 'Resubmit your KYC',
      description:
        'Your identity verification needs to be redone before you can invest. It only takes a couple of minutes.',
      ctaLabel: 'Resubmit KYC',
      onCta: resubmitKyc,
    }
  }
  if (accountState === ACCOUNT_STATE.REKYC_EXPIRED) {
    return {
      icon: 'shield',
      title: 'Renew your verification',
      description: 'Your identity verification has expired. Renew it to continue investing.',
      ctaLabel: 'Renew verification',
      onCta: resubmitKyc,
    }
  }
  // Terminal KYC rejection (no self-service re-KYC) — contact-us only, matching
  // the dashboard's kyc-rejected paused variant.
  if (accountState === ACCOUNT_STATE.PAUSED && pausedReason === PAUSED_REASON.KYC_REJECTED) {
    return {
      icon: 'lock',
      title: 'Verification unsuccessful',
      description:
        "We couldn't verify your identity. Please contact support so we can help you continue.",
      ctaLabel: 'Contact support',
      onCta: () => router.push(routes.HELP),
    }
  }
  if (kycComplete && bankComplete && walletActive) return null
  if (!kycComplete) {
    if (investorStatus === INVESTOR_STATUS.PENDING_REVIEW) {
      return {
        icon: 'eye',
        title: 'Your profile is under review',
        description:
          "Our team is reviewing your submission. You'll be notified once approved and can invest then.",
        ctaLabel: 'Go to dashboard',
        onCta: () => router.push(routes.DASHBOARD),
      }
    }
    return {
      icon: 'shield',
      title: 'Verify your identity',
      description: 'Complete KYC to start investing. It takes about 2 minutes.',
      ctaLabel: 'Start verification',
      onCta: () => router.push(routes.DASHBOARD),
    }
  }
  if (!bankComplete) {
    return {
      icon: 'building-columns',
      title: 'Add a verified bank account',
      description: 'Link a bank account so we can return your earnings.',
      ctaLabel: 'Add bank account',
      onCta: () => router.push(routes.BANK_ACCOUNTS),
    }
  }
  return {
    icon: 'wallet',
    title: 'Activating your wallet',
    description: 'Your platform wallet is being set up. Refresh in a moment.',
    ctaLabel: 'Refresh status',
    onCta: refetchWallet,
  }
}

/**
 * Derives whether the user can transact on this offering and — if not — the
 * single next blocker to surface. Wallet status is keyed by `propertyId` so the
 * trustline check is per-offering.
 */
export function useInvestEligibility(propertyId: string) {
  const router = useRouter()
  const accountStatusQuery = useAccountStatusQuery()
  const walletStatusQuery = useWalletStatusQuery(propertyId)
  const fiatWalletQuery = useFiatWalletQuery()
  const reInitiateKycMutation = useReInitiateKycMutation()

  const kycComplete = computed(() => accountStatusQuery.data.value?.kycStatus === 'KYC_COMPLETED')
  const bankComplete = computed(() => {
    const bank = accountStatusQuery.data.value?.bankAccount
    return Boolean(bank && bank.accountStatus === 'verified')
  })
  // `is_ready` already encodes "crypto wallet active AND trustline active" on
  // the new offering-readiness endpoint, so we lean on it instead of checking
  // wallet_status / trustline_status independently.
  const walletActive = computed(() =>
    Boolean(walletStatusQuery.data.value?.walletStatus === 'active'),
  )

  const investorStatus = computed(() => accountStatusQuery.data.value?.investorStatus)
  const accessRestricted = computed(() => accountStatusQuery.data.value?.accessRestricted ?? false)
  const accountState = computed(() => accountStatusQuery.data.value?.accountState ?? '')
  const pausedReason = computed(() => accountStatusQuery.data.value?.pausedReason ?? null)

  // Re-initiate KYC only on explicit CTA tap, then route into the resubmit
  // wizard — never on mount (that would flip the account to "under review"
  // before the user chooses to redo). Mirrors StateReKycRequiredContainer.
  const resubmitKyc = async () => {
    try {
      await reInitiateKycMutation.mutateAsync()
    } catch {
      return
    }
    router.push({ path: routes.ONBOARDING_WIZARD, query: { mode: 'resubmit' } })
  }

  const eligibilityFix = computed(() =>
    buildFix(kycComplete.value, bankComplete.value, walletActive.value, {
      router,
      refetchWallet: () => walletStatusQuery.refetch(),
      investorStatus: investorStatus.value,
      accessRestricted: accessRestricted.value,
      accountState: accountState.value,
      pausedReason: pausedReason.value,
      resubmitKyc,
    }),
  )
  // Eligible exactly when there's no blocker to surface — derive from the fix so
  // the two can never drift (a re-KYC user is blocked even though kyc/bank/
  // wallet booleans may all read "ready").
  const isEligible = computed(() => eligibilityFix.value === null)
  const walletBalance = computed(() => Number(fiatWalletQuery.data.value?.availableBalance ?? 0))

  return { isEligible, eligibilityFix, walletBalance }
}
